import { useQuery } from '@tanstack/react-query'
import { CalendarType } from '@xeetr/types/enums'
import { endOfMonth, startOfMonth } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useContext, useMemo } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { BookingService } from '../services/Bookings.service'
import CalendarService from '../services/Calendar.service'
import { DailyNoteService } from '../services/DailyNote.service'

import { useTenantOptionalService } from './common/useServiceFactory'


interface UseMonthlyOverviewProps {
  month: Date
}

export const useMonthlyOverview = ({ month }: UseMonthlyOverviewProps) => {
  const { tenant, tenantId } = useContext(ActiveTenantContext)
  const getBookingService = useTenantOptionalService(BookingService)
  const getDailyNoteService = useTenantOptionalService(DailyNoteService)
  const getCalendarService = useTenantOptionalService(CalendarService)

  const monthStart = useMemo(() => startOfMonth(month), [month])
  const monthEnd = useMemo(() => endOfMonth(month), [month])

  const bookingsQuery = useQuery({
    queryKey: queryKeys.monthlyOverview.bookings(tenantId, monthStart, monthEnd),
    queryFn: async () => {
      const service = await getBookingService()
      return service.getBookings(
        monthStart,
        monthEnd,
        'active',
        tenant?.timezone || 'Europe/Berlin'
      )
    },
  })

  const dailyNotesQuery = useQuery({
    queryKey: queryKeys.monthlyOverview.dailyNotes(tenantId, monthStart, monthEnd),
    queryFn: async () => {
      const service = await getDailyNoteService()
      return service.getDailyNotes(
        formatInTimeZone(monthStart, tenant?.timezone || 'Europe/Berlin', 'yyyy-MM-dd'),
        formatInTimeZone(monthEnd, tenant?.timezone || 'Europe/Berlin', 'yyyy-MM-dd')
      )
    },
  })

  const eventsQuery = useQuery({
    queryKey: queryKeys.monthlyOverview.events(tenantId, monthStart, monthEnd),
    queryFn: async () => {
      const service = await getCalendarService()
      return service.getCalendarEvents(monthStart, monthEnd, CalendarType.MONTH)
    },
  })

  const isLoading = bookingsQuery.isLoading || dailyNotesQuery.isLoading || eventsQuery.isLoading
  const isError = bookingsQuery.isError || dailyNotesQuery.isError || eventsQuery.isError
  const error = bookingsQuery.error || dailyNotesQuery.error || eventsQuery.error

  return {
    bookings: bookingsQuery.data ?? [],
    dailyNotes: dailyNotesQuery.data ?? [],
    events: eventsQuery.data ?? [],
    isLoading,
    isError,
    error,
  }
}
